/**
 * 跟读详情页 (数据来自接口 `GET /courses/{id}`).
 *
 * 跟读详情页包含的功能点:
 *   视频舞台(返回/收藏/字幕/全屏)
 *   句子卡片(序号/重点词高亮/中文/meta/复制/重听/收藏)
 *   底部控制栏(进度拖动/时间/倍速/上一句/播放暂停/下一句/盲听), 桌面在播放器下方显示句子导航
 *
 * 响应式:
 *   手机: 视频与标题/tab 固定在视口内, 仅句子区滚动, 控制栏占独立底行
 *   Pad 竖屏: 上下布局, 底部固定控制栏
 *   手机/Pad 横屏: 左侧视频与句子, 右侧简介与讲稿, 底部固定控制栏
 *   桌面横屏: 左侧视频与句子, 右侧简介与讲稿, 底部固定控制栏
 *
 * 句子区在所有断点都是单列: 一行一个句子, 不再在桌面分两列。
 *
 * 句子区不渲染整份列表: Pad 横屏展示 5 条, 其他布局展示 3 条,
 * 并随播放进度自动滚动; 句子区自身不可滚动时不做任何滚动,
 * 不能把整个页面滚到当前句, 否则顶栏会遮住视频。
 */

import { ApiError, api } from '../lib/api'
import { CONTENT_LANGUAGE_LABELS, currentLanguage } from '../lib/content-language'
import { highlightClass } from '../lib/difficulty'
import { clamp, formatTime } from '../lib/format'
import { courseFavorites } from '../lib/favorites'
import { renderMarkdown } from '../lib/markdown'
import {
  durationLabelOf,
  rangeLabel,
  toSentenceViews,
  type SentencePart,
  type SentenceView,
} from '../lib/lesson'
import { readLocalProgress, saveLocalProgress, toLocalProgress } from '../lib/progress'
import { authRef } from '../stores/auth'
import { goTo } from '../stores/router'
import { toast } from '../stores/toast'
import type { WordCardStore } from '../stores/word-card'
import type { ContentLanguage, CourseDetail, SentenceHighlight } from '../types/api'

const SPEED_LABELS = ['0.5x', '0.75x', '1.0x', '1.25x', '1.5x', '2.0x', '2.5x'] as const
const SPEED_VALUES = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5] as const
const PAD_LANDSCAPE_QUERY = '(orientation: landscape) and (min-width: 48rem) and (min-height: 48rem) and (pointer: coarse)'
const TALL_DESKTOP_QUERY = '(orientation: landscape) and (min-width: 64rem) and (min-height: 48rem)'

/** 进度写库节流: 播放中每 5s 同步一次到后端, 不做逐帧写库 */
const PROGRESS_SYNC_MS = 5000
/** localStorage 写入节流: 约每 2s 落一次本机缓存 */
const LOCAL_PROGRESS_MIN_INTERVAL_MS = 2000
/** 小于这个秒数的续播不值得跳转/提示 */
const RESUME_MIN_SECONDS = 3

export function practicePage() {
  // 播放器与列表容器放在闭包里, 避免被 Alpine 包成响应式代理
  let video: HTMLVideoElement | null = null
  let mediaRefresh: Promise<void> | null = null
  // 连续修改词表时只应用最后一次请求, 旧响应不能覆盖当前选择.
  let highlightRequest = 0
  let list: HTMLElement | null = null
  let viewportQuery: MediaQueryList | null = null
  let layoutQuery: MediaQueryList | null = null
  let tallQuery: MediaQueryList | null = null
  let handleViewportChange: (() => void) | null = null
  let handleFullscreenChange: (() => void) | null = null
  let syncTimer: number | null = null
  let controlsTimer: number | null = null
  let captionTrack: TextTrack | null = null
  // 控件显隐的闭包镜像: 只驱动字幕行位置, 不参与 Alpine 响应式重渲染
  let controlsVisible = true

  let handlePageHide: (() => void) | null = null
  let handleVisibilityChange: (() => void) | null = null
  // 每次进入资源只提示一次续播, 后端记录覆盖本机缓存时不重复弹
  let resumeNotified = false

  /**
   * 全屏时打开原生控件, 退出后关闭.
   *
   * 全屏的是 <video> 本身, 页面的自定义控制栏不在全屏元素内, 会自然隐藏;
   * 此时只有原生控件能播控, 所以需要按全屏状态切换 controls。
   * 直接调原生属性, 不读 Alpine 的 this, 方便安全地注册/移除监听。
   */
  const syncFullscreenControls = (): void => {
    const el = video
    if (!el) return
    const vendor = el as HTMLVideoElement & { webkitDisplayingFullscreen?: boolean }
    el.controls = document.fullscreenElement === el || vendor.webkitDisplayingFullscreen === true
  }

  return {
    // 资源级收藏(书签)与资源列表页共用同一套逻辑, 状态独立于句子收藏
    ...courseFavorites(),
    course: null as CourseDetail | null,
    sentences: [] as SentenceView[],
    activeId: 0,
    position: 0,
    isPlaying: false,
    playerControlsVisible: true,
    playerHovered: false,
    captionsEnabled: false,
    speedIndex: 2,
    blindListen: false,
    languageSaving: false,
    languageMenuOpen: false,
    languageOptions: [
      { value: 'bilingual', label: '双语' },
      { value: 'english', label: '英语' },
      { value: 'chinese', label: '中文' },
    ] satisfies { value: ContentLanguage; label: string }[],
    savedIds: [] as number[],
    revealedIds: [] as number[],
    loading: false,
    error: '',
    padLandscape: false,
    wideLayout: false,
    desktopTall: false,
    practiceTab: 'shadowing' as 'description' | 'script' | 'shadowing',

    // 进度: 本机缓存立即写, 后端每 5s 节流写; 续播目标在视频就绪后再 seek
    progressDirty: false,
    pendingResume: null as number | null,
    lastLocalSaveAt: 0,

    // 高亮与弹层: 高亮区间由接口返回; 单词卡片弹层是跨页面共享的 $store.wordCard
    highlights: [] as SentenceHighlight[],

    // 模板用的展示辅助(方法形式, Alpine 可直接调用)
    highlightClass,

    // --- 派生值 ---------------------------------------------------------
    get mediaUrl(): string {
      const course = this.course
      if (!course) return ''
      // 优先播放默认 480p; 缺少时使用 360p, 旧资源仍回退到 720p.
      return course.media_url_480p || course.media_url_360p || course.media_url_720p
    },


    /**
     * 跟读页内容语言: 英文模式只看英文, 中文模式只看中文, 双语模式中英并排.
     * 均为 getter, 用户切换语言后模板会自动重渲染.
     */
    get showEnglish(): boolean {
      return currentLanguage() !== 'chinese'
    },

    get showChinese(): boolean {
      return currentLanguage() !== 'english'
    },

    /** 纯中文模式: 句子卡片用译文充当主文本 */
    get chineseOnly(): boolean {
      return currentLanguage() === 'chinese'
    },

    /** 跟读页主标题: 中文模式取译文, 其余取英文原标题 */
    get practiceTitle(): string {
      const course = this.course
      if (!course) return '跟读'
      if (this.chineseOnly) return course.title_translation || course.title
      return course.title
    },

    get contentLanguage(): ContentLanguage {
      return currentLanguage()
    },

    /** 关闭弹层后把键盘焦点还给入口. */
    closeLanguageMenu() {
      this.languageMenuOpen = false
      document.querySelector<HTMLButtonElement>('[x-ref="languageTrigger"]')?.focus()
    },

    /** 将键盘焦点保留在语言选择弹层内. */
    trapLanguageFocus(event: KeyboardEvent) {
      const dialog = event.currentTarget as HTMLElement
      const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('.modal-box button:not(:disabled)'))
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    },

    /** 显式选择内容语言, 与设置页共用 users.language. */
    async setLanguage(next: ContentLanguage): Promise<void> {
      const store = authRef()
      if (!store || this.languageSaving) return
      if (next === currentLanguage()) {
        this.closeLanguageMenu()
        return
      }
      this.languageSaving = true
      try {
        const ok = await store.updateProfile({ language: next })
        if (ok) {
          this.syncCaptions()
          this.closeLanguageMenu()
          toast(`已切换为${CONTENT_LANGUAGE_LABELS[next]}`)
        } else {
          toast(store.error || '语言切换失败')
          store.clearError()
        }
      } finally {
        this.languageSaving = false
      }
    },

    /** 简介英文是否可见: 中文模式且没有译文时回退英文 */
    get descriptionEnVisible(): boolean {
      return this.showEnglish || !this.course?.description_translation
    },

    /** 简介中文是否可见: 英文模式隐藏; 没有译文时隐藏 */
    get descriptionZhVisible(): boolean {
      return this.showChinese && Boolean(this.course?.description_translation)
    },

    /** 是否生成了讲稿: 有正文才显示「讲稿」tab */
    get hasScript(): boolean {
      return Boolean(this.course?.script || this.course?.script_translation)
    },

    /**
     * 讲稿正文使用的语言版本.
     *
     * 与简介不同: 双语模式不并列两份讲稿(太长), 只展示英文; 中文模式用中文, 缺失回退英文。
     */
    get scriptLang(): 'en' | 'zh-CN' {
      return this.chineseOnly && this.course?.script_translation ? 'zh-CN' : 'en'
    },

    /** 当前语言下的讲稿原文(未渲染) */
    get scriptSource(): string {
      const course = this.course
      if (!course) return ''
      if (this.scriptLang === 'zh-CN') return course.script_translation
      return course.script
    },

    /** 讲稿渲染结果; 语言切换后 scriptSource 变化会重新渲染 */
    get scriptHtml(): string {
      return renderMarkdown(this.scriptSource)
    },

    get durationSeconds(): number {
      return this.course?.duration_seconds ?? 0
    },

    get publishedDateLabel(): string {
      return this.course?.source_published_at ?? ''
    },

    get currentLabel(): string {
      return formatTime(this.position)
    },

    get durationLabel(): string {
      return durationLabelOf(this.durationSeconds)
    },

    get progressPercent(): number {
      if (this.durationSeconds <= 0) return 0
      return clamp((this.position / this.durationSeconds) * 100, 0, 100)
    },

    get speedLabel(): string {
      return SPEED_LABELS[this.speedIndex] ?? '1.0x'
    },

    get activeIndex(): number {
      return this.sentences.findIndex((sentence) => sentence.id === this.activeId) + 1
    },

    get savedCount(): number {
      return this.savedIds.length
    },

    get sentenceWindow(): number {
      return this.padLandscape ? 5 : 3
    },

    /** 当前句尽量居中, 首尾处窗口向内收 */
    get visibleSentences(): SentenceView[] {
      // PC 高窗口展示整份句子表, 可自由滚动回看; 其他布局保持紧凑窗口
      if (this.desktopTall) return this.sentences
      const all = this.sentences
      const count = this.sentenceWindow
      if (all.length <= count) return all

      const index = all.findIndex((sentence) => sentence.id === this.activeId)
      if (index < 0) return all.slice(0, count)

      const start = clamp(index - Math.floor(count / 2), 0, all.length - count)
      return all.slice(start, start + count)
    },

    rangeLabel,

    // --- 生命周期 -------------------------------------------------------
    init(): void {
      viewportQuery = window.matchMedia(PAD_LANDSCAPE_QUERY)
      layoutQuery = window.matchMedia('(orientation: landscape) and (min-width: 40rem)')
      tallQuery = window.matchMedia(TALL_DESKTOP_QUERY)
      handleViewportChange = () => {
        this.padLandscape = viewportQuery?.matches ?? false
        this.wideLayout = layoutQuery?.matches ?? false
        this.desktopTall = tallQuery?.matches ?? false
        // 断点切换会重建句子列表, 重新把当前句滚回视野
        if (this.desktopTall) {
          window.setTimeout(() => this.scrollActiveIntoView(), 120)
        }
      }
      handleViewportChange()
      viewportQuery.addEventListener('change', handleViewportChange)
      layoutQuery.addEventListener('change', handleViewportChange)
      tallQuery.addEventListener('change', handleViewportChange)

      // 进度同步: 定时把待同步的进度写库; 页面隐藏/关闭前再冲一次, 避免丢最后几秒
      syncTimer = window.setInterval(() => {
        void this.flushProgress()
      }, PROGRESS_SYNC_MS)
      handlePageHide = () => {
        void this.flushProgress()
      }
      handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') void this.flushProgress()
      }
      window.addEventListener('pagehide', handlePageHide)
      document.addEventListener('visibilitychange', handleVisibilityChange)
    },

    mount(videoEl: HTMLVideoElement, listEl: HTMLElement) {
      video = videoEl
      list = listEl
      handleFullscreenChange = () => {
        syncFullscreenControls()
        this.syncCaptions()
      }
      videoEl.addEventListener('fullscreenchange', handleFullscreenChange)
      videoEl.addEventListener('webkitbeginfullscreen', handleFullscreenChange)
      videoEl.addEventListener('webkitendfullscreen', handleFullscreenChange)
      captionTrack = videoEl.addTextTrack('subtitles', '跟读字幕')
      this.syncCaptions()
      this.applyPendingResume()
    },

    destroy() {
      this.clearControlsTimer()
      captionTrack = null
      if (viewportQuery && handleViewportChange) {
        viewportQuery.removeEventListener('change', handleViewportChange)
      }
      if (layoutQuery && handleViewportChange) {
        layoutQuery.removeEventListener('change', handleViewportChange)
      }
      if (tallQuery && handleViewportChange) {
        tallQuery.removeEventListener('change', handleViewportChange)
      }
      if (video && handleFullscreenChange) {
        video.removeEventListener('fullscreenchange', handleFullscreenChange)
        video.removeEventListener('webkitbeginfullscreen', handleFullscreenChange)
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange)
      }
      viewportQuery = null
      layoutQuery = null
      tallQuery = null
      handleViewportChange = null
      handleFullscreenChange = null
      if (syncTimer !== null) {
        window.clearInterval(syncTimer)
        syncTimer = null
      }
      if (handlePageHide) window.removeEventListener('pagehide', handlePageHide)
      if (handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
      handlePageHide = null
      handleVisibilityChange = null
      video = null
      list = null
    },

    /** 路由参数变化时加载资源 (x-effect 绑定) */
    async syncCourse(): Promise<void> {
      const router = window.Alpine?.store('router') as
        | { params: Record<string, string> }
        | undefined
      const rawId = router?.params['id']
      const courseId = Number(rawId ?? 0)
      if (!courseId) return
      if (courseId === this.course?.id) {
        try {
          await this.refreshMediaIfNeeded()
        } catch {
          toast('播放链接刷新失败, 请重新进入资源')
        }
        return
      }

      this.languageMenuOpen = false
      this.loading = true
      this.error = ''
      try {
        const detail = await api.courses.detail(courseId)
        this.practiceTab = 'shadowing'
        this.playerControlsVisible = true
        controlsVisible = true
        this.clearControlsTimer()
        this.course = detail
        this.highlights = []
        this.sentences = toSentenceViews(detail.sentences)
        this.activeId = this.sentences[0]?.id ?? 0
        this.position = 0
        this.revealedIds = []
        this.isPlaying = false
        // 新资源: 清掉上一条的续播与待同步状态
        this.pendingResume = null
        this.progressDirty = false
        this.lastLocalSaveAt = 0
        resumeNotified = false
        // 句子收藏状态在后端; 拉取失败不影响跟读, 退化成「都未收藏」
        // (单词卡片的收藏状态由 $store.wordCard 自己维护)
        try {
          this.savedIds = await api.favorites.savedIds('sentence')
        } catch {
          this.savedIds = []
        }
        await this.loadSavedCourseIds()
        if (video) video.playbackRate = SPEED_VALUES[this.speedIndex] ?? 1
        await this.resumeProgress(courseId)
      } catch (error) {
        this.course = null
        this.sentences = []
        this.error = error instanceof ApiError ? error.message : '资源加载失败'
      } finally {
        this.loading = false
      }
    },

    /**
     * 按当前高亮词表拉取整份资源的高亮区间, 并重建句子片段.
     *
     * 单独成方法并由路由 x-effect 触发: 用户改完高亮配置重进跟读页时只需重拉这一份,
     * 不重新下载整个资源详情, 也不打断播放状态。失败降级为「不高亮」, 不影响跟读。
     */
    async syncHighlights(): Promise<void> {
      const course = this.course
      const request = ++highlightRequest
      if (!course) return
      try {
        const spans = await api.courses.highlights(course.id)
        if (request !== highlightRequest || this.course?.id !== course.id) return
        this.highlights = spans
        this.sentences = toSentenceViews(course.sentences, spans)
      } catch {
        if (request !== highlightRequest || this.course?.id !== course.id) return
        this.highlights = []
        this.sentences = toSentenceViews(course.sentences)
      }
    },

    /** 高亮词样式; 普通片段保持正文色 */
    partClass(part: SentencePart): string {
      if (!part.highlight) return 'text-ink'
      return `cursor-pointer rounded px-0.5 transition-colors ${highlightClass(part.difficulty ?? '')}`
    },

    /** 点高亮词弹词卡; 点普通文字不拦截, 让整句的选中/播放照常触发 */
    onPartClick(event: MouseEvent, part: SentencePart): void {
      if (!part.highlight || !part.wordId) return
      event.stopPropagation()
      const store = window.Alpine?.store('wordCard') as WordCardStore | undefined
      void store?.show(part.wordId)
    },

    // --- 跟读进度 -------------------------------------------------------
    /** 进入资源后: 先用本机缓存瞬间续播, 再拉后端记录覆盖(跨设备以最新为准) */
    async resumeProgress(courseId: number): Promise<void> {
      const userId = authRef()?.user?.id ?? 0
      const local = readLocalProgress(userId, courseId)
      if (local && local.positionSeconds > 0) {
        this.queueResume(local.positionSeconds, local.sentenceId)
      }

      try {
        const server = await api.progress.get(courseId)
        // 路由可能已经切走, 旧响应不能覆盖新资源
        if (!server || this.course?.id !== courseId) return
        const localAt = local?.updatedAt ?? 0
        const serverAt = Date.parse(server.updated_at) || 0
        if (server.position_seconds > 0 && serverAt >= localAt) {
          this.queueResume(server.position_seconds, server.sentence_id)
          saveLocalProgress(userId, courseId, toLocalProgress(server))
        } else if (local) {
          // 本机进度比后端新: 标脏, 下一次同步把它推上去
          this.progressDirty = true
        }
      } catch {
        // 网络失败时本机缓存已经够用
      }
    },

    /** 排队一次续播; 视频还没就绪时先记下, 等 loadedmetadata 再 seek */
    queueResume(position: number, sentenceId: number): void {
      if (position <= 0) return
      // 句子高亮与进度条不依赖视频元数据, 立即反映出来
      const sentence = this.sentences.find((item) => item.id === sentenceId)
      if (sentence) this.activeId = sentence.id
      this.position = position
      const el = video
      if (el && el.readyState >= 1 && Math.abs(el.currentTime - position) < 1) return
      this.pendingResume = position
      requestAnimationFrame(() => this.applyPendingResume())
    },

    /** 把排队的续播位置落到播放器; 元数据未就绪时挂一次性监听 */
    applyPendingResume(): void {
      const pending = this.pendingResume
      const el = video
      if (pending === null || !el) return
      this.pendingResume = null
      const limit = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : pending
      let target = clamp(pending, 0, limit)
      // 上次已看完整段: 直接从开头重看, 不要停在没有可播内容的结尾
      if (el.duration > 0 && target >= el.duration - 1) target = 0

      const mark = () => {
        el.currentTime = target
        this.position = el.currentTime
        this.scrollActiveIntoView()
      }
      if (el.readyState >= 1) mark()
      else el.addEventListener('loadedmetadata', mark, { once: true })

      if (!resumeNotified && target >= RESUME_MIN_SECONDS) {
        resumeNotified = true
        toast(`已从 ${formatTime(target)} 继续跟读`)
      }
    },

    /** 播放推进时更新本机缓存并标脏(节流, 避免每次 timeupdate 都写盘) */
    markProgress(force = false): void {
      const el = video
      const user = authRef()?.user
      if (!el || !user || !this.course) return
      const now = Date.now()
      if (!force && now - this.lastLocalSaveAt < LOCAL_PROGRESS_MIN_INTERVAL_MS) return
      this.lastLocalSaveAt = now
      saveLocalProgress(user.id, this.course.id, {
        positionSeconds: el.currentTime,
        sentenceId: this.activeId,
        updatedAt: now,
      })
      this.progressDirty = true
    },

    /** 把待同步的进度写库; 失败保留 dirty, 下一次 tick 重试 */
    async flushProgress(): Promise<void> {
      const el = video
      const course = this.course
      if (!this.progressDirty || !el || !course) return
      this.progressDirty = false
      const position = Math.max(0, Math.round(el.currentTime * 10) / 10)
      try {
        await api.progress.save(course.id, position, this.activeId)
      } catch {
        this.progressDirty = true
      }
    },

    // --- 播放 -----------------------------------------------------------
    async refreshMediaIfNeeded(): Promise<void> {
      if (mediaRefresh) return mediaRefresh
      const el = video
      const course = this.course
      if (!el || !course || !this.mediaUrl) return
      const key = new URL(this.mediaUrl, window.location.origin).searchParams.get('auth_key')
      if (!key || Date.now() / 1000 - Number(key.split('-')[0]) < 5 * 3600) return
      // 链接刷新会触发页面 x-effect, 共享一次请求避免重复加载和进度重置.
      mediaRefresh = (async () => {
        const resumeAt = el.currentTime
        const wasPlaying = !el.paused
        const detail = await api.courses.detail(course.id)
        el.pause()
        await new Promise<void>((resolve, reject) => {
          const cleanup = () => {
            clearTimeout(timeout)
            el.removeEventListener('loadedmetadata', loaded)
            el.removeEventListener('seeked', resumed)
            el.removeEventListener('error', failed)
          }
          const resumed = () => { cleanup(); resolve() }
          const loaded = () => {
            el.playbackRate = SPEED_VALUES[this.speedIndex] ?? 1
            if (resumeAt > 0) {
              el.addEventListener('seeked', resumed, { once: true })
              el.currentTime = Math.min(resumeAt, el.duration)
            } else resumed()
          }
          const failed = () => { cleanup(); reject(new Error('媒体加载失败')) }
          const timeout = window.setTimeout(failed, 15000)
          el.addEventListener('loadedmetadata', loaded, { once: true })
          el.addEventListener('error', failed)
          // src 由 Alpine 绑定更新一次; 手动赋值会触发二次加载.
          this.course = detail
        })
        if (wasPlaying) await el.play()
      })()
      try {
        await mediaRefresh
      } finally {
        mediaRefresh = null
      }
    },

    /** 暂停时常显, 播放时点击画面临时显示控件. */
    showPlayerControls(): void {
      this.clearControlsTimer()
      this.playerControlsVisible = true
      controlsVisible = true
      this.updateCaptionLine()
      if (this.isPlaying && !this.playerHovered) {
        controlsTimer = window.setTimeout(() => {
          this.playerControlsVisible = false
          controlsVisible = false
          this.updateCaptionLine()
          controlsTimer = null
        }, 3000)
      }
    },

    /** 控制区显隐时只调整已有字幕行位置, 不重建整条字幕轨 */
    updateCaptionLine(): void {
      if (!captionTrack) return
      const line = video?.controls ? 'auto' : controlsVisible ? -5 : -1
      for (const cue of Array.from(captionTrack.cues ?? [])) {
        ;(cue as VTTCue).line = line
      }
    },

    /** 桌面端鼠标悬停播放器时保持控件显示; 离开后再按播放状态自动隐藏 */
    onPlayerHover(entered: boolean): void {
      if (!window.matchMedia('(pointer: fine)').matches) return
      this.playerHovered = entered
      this.showPlayerControls()
    },

    /**
     * 点击画面切换播放/暂停, 手机与 PC/Pad 交互一致.
     * 进度条与按钮内部各自 stop 冒泡, 不会误触这里.
     */
    onStageClick(): void {
      this.showPlayerControls()
      void this.togglePlay()
    },

    /** 防止离页或暂停后仍被旧定时器隐藏控件. */
    clearControlsTimer(): void {
      if (controlsTimer !== null) window.clearTimeout(controlsTimer)
      controlsTimer = null
    },

    /** 复用句子时间轴生成原生字幕, 语言与内容设置一致. */
    syncCaptions(): void {
      const language = currentLanguage()
      const sentences = this.sentences
      const showing = this.captionsEnabled && !this.blindListen
      if (!captionTrack) return
      captionTrack.mode = 'hidden'
      for (const cue of Array.from(captionTrack.cues ?? [])) captionTrack.removeCue(cue)
      for (const [index, sentence] of sentences.entries()) {
        if (sentence.end <= sentence.start) continue
        const nextStart = sentences[index + 1]?.start
        // 数据里的相邻句子有 1-3 秒重叠; 原生字幕在重叠区间会同时显示多条,
        // 这里截到下一句开始前, 保证任意时刻只有当前句字幕.
        const end =
          nextStart !== undefined && nextStart > sentence.start
            ? Math.min(sentence.end, nextStart - 0.05)
            : sentence.end
        if (end <= sentence.start) continue
        const text = language === 'chinese'
          ? sentence.translation || sentence.text
          : language === 'bilingual'
            ? [sentence.text, sentence.translation].filter(Boolean).join('\n')
            : sentence.text
        // VTTCue 将尖括号解析为标记, 转义以保留原句文本.
        const escaped = text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
        const cue = new VTTCue(sentence.start, end, escaped)
        // 控件出现时抬高字幕, 避免底部按钮覆盖文字; 原生全屏由浏览器排版.
        cue.line = video?.controls ? 'auto' : controlsVisible ? -5 : -1
        captionTrack.addCue(cue)
      }
      captionTrack.mode = showing ? 'showing' : 'disabled'
    },

    /** 字幕仅在本次跟读中切换, 不新增持久化设置. */
    toggleCaptions(): void {
      this.captionsEnabled = !this.captionsEnabled
      this.showPlayerControls()
    },

    async togglePlay() {
      const el = video
      if (!el) return

      if (el.paused) {
        try {
          await this.refreshMediaIfNeeded()
          await el.play()
          this.isPlaying = true
        } catch {
          // 浏览器可能因自动播放策略拒绝, 保持暂停状态即可
          this.isPlaying = false
        }
      } else {
        el.pause()
        this.isPlaying = false
      }
    },

    onPlay() {
      this.isPlaying = true
      this.clearControlsTimer()
      this.playerControlsVisible = false
      controlsVisible = false
      this.updateCaptionLine()
    },

    onPause() {
      this.isPlaying = false
      this.showPlayerControls()
      // 暂停是明确的用户动作, 立即把当前位置写库
      void this.flushProgress()
    },

    onEnded() {
      this.isPlaying = false
      this.showPlayerControls()
      void this.flushProgress()
    },

    /** 离开跟读页时暂停, 避免视频在后台继续播放出声 (由路由 x-effect 触发) */
    stopPlayback(): void {
      this.languageMenuOpen = false
      if (!video) return
      video.pause()
      this.isPlaying = false
      this.showPlayerControls()
      void this.flushProgress()
    },

    /** 播放推进时同步进度条与"当前句"高亮 */
    onTimeUpdate() {
      const el = video
      if (!el) return

      this.position = el.currentTime
      const current = this.sentences.find(
        (sentence) => el.currentTime >= sentence.start && el.currentTime < sentence.end,
      )
      if (current && current.id !== this.activeId) {
        this.activeId = current.id
        // 窗口会随当前句平移, 跟一句滚一句
        this.scrollActiveIntoView()
      }
      this.markProgress()
    },

    /** 原生滑块支持触屏拖动和键盘定位, 续播仍走统一进度保存. */
    seekPercent(percent: number) {
      const el = video
      if (!el || !Number.isFinite(percent) || this.durationSeconds <= 0) return
      el.currentTime = clamp(percent, 0, 100) / 100 * this.durationSeconds
      this.position = el.currentTime
      this.markProgress(true)
    },

    cycleSpeed() {
      this.speedIndex = (this.speedIndex + 1) % SPEED_VALUES.length
      if (video) video.playbackRate = SPEED_VALUES[this.speedIndex] ?? 1
      toast(`语速 ${this.speedLabel}`)
    },

    /**
     * 全屏切换.
     *
     * 标准 Fullscreen API 在部分 iOS/iPadOS Safari 上只对 <video> 暴露 webkit 前缀接口,
     * 因此优先走 window 的标准入口, 失败或不可用时回退 webkitEnterFullscreen;
     * 全屏后由浏览器接管画面, 页面上的自定义控制栏自然隐藏。
     */
    toggleFullscreen(): void {
      const el = video
      if (!el) return

      const vendorVideo = el as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void
      }
      const vendorDocument = document as Document & {
        webkitFullscreenElement?: Element | null
        webkitExitFullscreen?: () => void
      }

      if (document.fullscreenElement ?? vendorDocument.webkitFullscreenElement) {
        if (document.exitFullscreen) {
          void document.exitFullscreen().catch(() => undefined)
        } else {
          vendorDocument.webkitExitFullscreen?.()
        }
        return
      }

      if (document.fullscreenEnabled && el.requestFullscreen) {
        void el.requestFullscreen().catch(() => {
          vendorVideo.webkitEnterFullscreen?.()
        })
        return
      }

      vendorVideo.webkitEnterFullscreen?.()
    },

    // --- 句子 -----------------------------------------------------------
    select(sentence: SentenceView) {
      this.activeId = sentence.id
      if (video) {
        video.currentTime = sentence.start
        this.position = sentence.start
      }
      this.markProgress(true)
      this.scrollActiveIntoView()
    },

    /** 点击卡片: 盲听模式下先显示原文, 否则跳转到该句 */
    onCardClick(sentence: SentenceView) {
      if (this.blindListen && !this.isRevealed(sentence.id)) {
        this.revealedIds.push(sentence.id)
        return
      }
      this.select(sentence)
    },

    step(offset: number) {
      const index = this.sentences.findIndex((sentence) => sentence.id === this.activeId)
      const next = this.sentences[index + offset]
      if (next) this.select(next)
    },

    async replay(sentence: SentenceView) {
      this.select(sentence)
      const el = video
      if (!el) return
      try {
        await this.refreshMediaIfNeeded()
        await el.play()
        this.isPlaying = true
      } catch {
        this.isPlaying = false
      }
    },

    scrollActiveIntoView() {
      if (!list) return
      const id = this.activeId
      // 等 Alpine 把窗口重渲染完再量位置, 否则滚到的是卡片挪动前的位置
      requestAnimationFrame(() => {
        const container = list
        const node = container?.querySelector<HTMLElement>(`[data-sentence-id="${id}"]`)
        if (!container || !node) return
        // 句子区不可滚动时什么都不做: 三到五条卡片已经整块可见,
        // 这里若回退到 node.scrollIntoView 会滚动整个页面, 顶栏会把视频顶部盖住
        if (container.scrollHeight <= container.clientHeight) return
        const top = node.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
        container.scrollTo({
          top: Math.max(0, top - (container.clientHeight - node.offsetHeight) / 2),
          behavior: 'smooth',
        })
      })
    },

    // --- 卡片操作 -------------------------------------------------------
    isSaved(id: number): boolean {
      return this.savedIds.includes(id)
    },

    async toggleSave(sentence: SentenceView) {
      const wasSaved = this.isSaved(sentence.id)
      const rollback = () => {
        this.savedIds = wasSaved
          ? [...this.savedIds, sentence.id]
          : this.savedIds.filter((id) => id !== sentence.id)
      }

      // 先乐观更新: 收藏是轻操作, 不该为了一个图标等一个来回
      this.savedIds = wasSaved
        ? this.savedIds.filter((id) => id !== sentence.id)
        : [...this.savedIds, sentence.id]

      try {
        if (wasSaved) {
          await api.favorites.remove('sentence', sentence.id)
        } else {
          await api.favorites.add('sentence', sentence.id)
        }
        toast(wasSaved ? '已取消收藏' : '已收藏')
      } catch (error) {
        rollback()
        toast(error instanceof ApiError ? error.message : '操作失败, 请重试')
      }
    },

    /** 复制当前阅读语言的完整 Markdown, 保留标题和段落格式. */
    async copyScript() {
      if (!this.scriptSource) return
      try {
        await navigator.clipboard.writeText(this.scriptSource)
        toast('讲稿 Markdown 已复制')
      } catch {
        toast('复制失败, 请长按选择文本')
      }
    },

    async copy(sentence: SentenceView) {
      try {
        // 复制当前语言的主文本: 中文模式复制译文, 其余复制英文原句
        const text = this.chineseOnly ? sentence.translation : sentence.text
        await navigator.clipboard.writeText(text)
        toast('已复制到剪贴板')
      } catch {
        toast('复制失败, 请长按选择文本')
      }
    },

    // --- 盲听 -----------------------------------------------------------
    toggleBlindListen() {
      this.blindListen = !this.blindListen
      if (!this.blindListen) this.revealedIds = []
      toast(this.blindListen ? '盲听模式已开启' : '盲听模式已关闭')
    },

    isRevealed(id: number): boolean {
      return this.revealedIds.includes(id)
    },

    /** 盲听且未揭示时, 英文句子模糊显示 */
    isTextHidden(id: number): boolean {
      return this.blindListen && !this.isRevealed(id)
    },

    backToCourses(): void {
      goTo('courses')
    },
  }
}
